import React from "react";
import { Alert } from "../../../../ui/components/Alert";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { QueryStatusBadge } from "../../../../ui/components/QueryStatusBadge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherArrowUpRight } from "@subframe/core";
import { FeatherBrain } from "@subframe/core";
import { FeatherCheck } from "@subframe/core";
import { FeatherClock } from "@subframe/core";
import { FeatherFileText } from "@subframe/core";
import { FeatherGitBranch } from "@subframe/core";
import { FeatherHelpCircle } from "@subframe/core";
import { FeatherUserPlus } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherDownload } from "@subframe/core";
import { formatDate, getRelativeTime } from "../../../../utils";
import QueryRaiseModal from "./QueryRaiseModal";
import { useAuth } from "../../../../features/auth";

// Clean Architecture: Use hooks and entities
import { useQueryDetails } from "../hooks/useQueryDetails";
import { useAddComment } from "../hooks/useAddComment";
import { useResolveQuery } from "../hooks/useResolveQuery";
import { useQueryNavigation } from "../hooks/useQueryNavigation";
import { QueryEntity, QueryStatus } from "../../domain/entities/QueryEntity";
import { getSourceNavigationPath } from "../../../../features/client/queries/presentation/utils/navigationHelpers";

const formatTime = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};
interface QueryDetailModalProps {
  open: boolean;
  isSubQuery?: boolean;
  onOpenChange: (open: boolean) => void;
  queryId?: number;
  onSubQueryUpdate?: (
    subQueryId: number,
    updates: Partial<QueryEntity>
  ) => void;
  onQueryResolved?: () => void;
  hideViewSourceButton?: boolean;
}

const QueryDetailModal: React.FC<QueryDetailModalProps> = ({
  open,
  isSubQuery,
  onOpenChange,
  queryId,
  onSubQueryUpdate,
  onQueryResolved,
  hideViewSourceButton = false,
}) => {
  // Clean Architecture: Use hooks instead of direct API calls
  const { query, loading, refetch } = useQueryDetails(queryId);
  const { addComment, adding: submittingComment } = useAddComment();
  const { resolve: resolveQuery, resolving: isResolving } = useResolveQuery();
  const { navigateToSource, canNavigate } = useQueryNavigation();
  const { user: currentUser } = useAuth();

  const [comment, setComment] = React.useState("");
  const [isRaiseQueryModalOpen, setIsRaiseQueryModalOpen] =
    React.useState(false);
  const [isSubQueryModalOpen, setIsSubQueryModalOpen] = React.useState(false);
  const [selectedSubQueryId, setSelectedSubQueryId] = React.useState<
    number | undefined
  >();
  const [resolveError, setResolveError] = React.useState<string | null>(null);

  // Local query state for optimistic updates
  const [localQuery, setLocalQuery] = React.useState<QueryEntity | null>(null);

  // Always refetch when modal opens (when open changes from false to true)
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current && queryId) {
      // Modal just opened - always refetch to get latest data including attachments
      refetch();
    }
    prevOpenRef.current = open;
  }, [open, queryId, refetch]);

  // Clear local query when modal closes
  React.useEffect(() => {
    if (!open) {
      setLocalQuery(null);
    }
  }, [open]);

  // Update local query when the fetched query changes
  React.useEffect(() => {
    if (query) {
      setLocalQuery(query);
    }
  }, [query]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !queryId || submittingComment) return;

    const result = await addComment(queryId, comment.trim());

    if (result.isSuccess) {
      setComment("");

      // Optimistically update local query with the new comment
      if (localQuery && result.value) {
        const newComment = result.value;
        setLocalQuery({
          ...localQuery,
          comments: [...(localQuery.comments || []), newComment],
        });
      }

      // Don't refetch - the comment is already added optimistically
      // This prevents the loading state and provides instant feedback
    } else {
      console.error("Failed to add comment:", result.error);
    }
  };

  const handleMarkResolved = async () => {
    if (!localQuery?.queryId) return;

    // Clear any previous errors
    setResolveError(null);

    const result = await resolveQuery(localQuery.queryId);

    if (result.isSuccess) {
      // Optimistically update the local query state
      setLocalQuery({
        ...localQuery,
        status: QueryStatus.RESOLVED,
        updatedAt: new Date(),
      });

      // If this is a subquery, notify the parent
      if (isSubQuery && onSubQueryUpdate) {
        onSubQueryUpdate(localQuery.queryId, result.value);
      }

      // Call the onQueryResolved callback to notify parent to refetch queries
      if (onQueryResolved) {
        onQueryResolved();
      }
    } else {
      // Show error in the modal instead of console.error
      setResolveError(
        result.error || "Failed to resolve query. Please try again."
      );
    }
  };

  const handleCreateNewQuery = () => {
    setIsRaiseQueryModalOpen(true);
  };

  const handleSubQueryClick = (subQueryId: number) => {
    setSelectedSubQueryId(subQueryId);
    setIsSubQueryModalOpen(true);
  };

  const handleSubQueryUpdate = (
    subQueryId: number,
    updates: Partial<QueryEntity>
  ) => {
    // Refetch to get updated data
    refetch();
  };

  const handleViewSourceDocument = () => {
    // if (localQuery && canNavigate(localQuery)) {
    //   navigateToSource(localQuery, { returnTo: "queries" });
    // }
    if (localQuery && canNavigate(localQuery)) {
      const path = getSourceNavigationPath(localQuery.source, localQuery.sourceId);
      if (!path) return;

      // Build URL with query parameters for routes that need documentId
      let url = path;
      if (localQuery.sourceId && (localQuery.source === "invoice" || localQuery.source === "bill")) {
        const separator = path.includes("?") ? "&" : "?";
        url = `${path}${separator}documentId=${localQuery.sourceId}`;
      }

      // Open in a new tab
      window.open(url, "_blank");
    }
  };

  // Check if current user can resolve the query (only the user who raised it)
  const canResolveQuery = () => {
    if (!currentUser || !localQuery) return false;
    // Compare by id (convert string to number) or email as unique identifiers
    const currentUserId = parseInt(currentUser.id);
    return (
      currentUserId === localQuery.raisedBy.id ||
      currentUser.email === localQuery.raisedBy.email ||
      currentUser.userName === localQuery.raisedBy.userName
    );
  };

  if (!query && !loading) {
    return (
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-full justify-center py-8">
          <span className="text-body text-neutral-500">Query not found</span>
        </div>
      </DialogLayout>
    );
  }

  // Only show loading state if we have no local query data yet
  // This prevents showing "Loading..." when refetching with existing data
  if (!localQuery && loading) {
    return (
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-full justify-center py-8">
          <span className="text-body text-neutral-500">Loading...</span>
        </div>
      </DialogLayout>
    );
  }

  // If we don't have localQuery and not loading, query not found
  if (!localQuery) {
    return (
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-full justify-center py-8">
          <span className="text-body text-neutral-500">Query not found</span>
        </div>
      </DialogLayout>
    );
  }

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-[640px] max-w-full flex-col items-start gap-6 bg-white px-8 py-8">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              QueryId-{localQuery.queryId}
            </span>
            <QueryStatusBadge
              status={localQuery.status}
              icon={
                localQuery.status === "open" ? (
                  <FeatherClock />
                ) : localQuery.status === "in_progress" ? (
                  <FeatherClock />
                ) : localQuery.status === "resolved" ? (
                  <FeatherCheck />
                ) : (
                  <FeatherX />
                )
              }
            >
              {localQuery.status.charAt(0).toUpperCase() +
                localQuery.status.slice(1).replace("_", " ")}
            </QueryStatusBadge>
          </div>

          <div className="flex items-center gap-2">
            {localQuery.status !== "resolved" && (
              <Button
                variant="neutral-tertiary"
                icon={<FeatherUserPlus />}
                onClick={handleCreateNewQuery}
              >
                Create New Query
              </Button>
            )}
            {localQuery.status !== "resolved" && canResolveQuery() && (
              <Button
                icon={<FeatherCheck />}
                onClick={handleMarkResolved}
                loading={isResolving}
                disabled={isResolving}
              >
                Mark as Resolved
              </Button>
            )}
            <IconButton icon={<FeatherX />} onClick={handleClose} />
          </div>
        </div>

        {/* Error Alert for resolve failures */}
        {resolveError && (
          <Alert
            variant="error"
            icon={<FeatherX />}
            title="Failed to Resolve Query"
            description={resolveError}
          />
        )}

        {/* Source Document Section */}
        {localQuery.source !== "general" &&
          localQuery.source !== "compliance" &&
          localQuery.sourceId && (
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <IconWithBackground icon={<FeatherFileText />} />
                  <span className="text-body-bold font-body-bold text-default-font">
                    {localQuery.source.toUpperCase()}-
                    {localQuery.sourceDetails?.billJson?.bill_number ||
                      localQuery.sourceDetails?.invoiceJson?.invoice_number ||
                      localQuery.sourceId}
                  </span>
                </div>
                {!hideViewSourceButton && (
                  <>
                    {canNavigate(localQuery) ? (
                      <Button
                        variant="brand-secondary"
                        size="small"
                        icon={<FeatherArrowUpRight />}
                        onClick={handleViewSourceDocument}
                      >
                        View Document
                      </Button>
                    ) : localQuery.sourceDetails?.s3SignedUrl ? (
                      <Button
                        variant="brand-secondary"
                        size="small"
                        icon={<FeatherArrowUpRight />}
                        onClick={() =>
                          window.open(
                            localQuery.sourceDetails?.s3SignedUrl,
                            "_blank"
                          )
                        }
                      >
                        View PDF
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
              <div className="flex w-full items-center gap-4">
                <Badge>
                  {localQuery.source.charAt(0).toUpperCase() +
                    localQuery.source.slice(1)}
                </Badge>
                {/* <span className="text-body font-body text-neutral-500">
                ID: {localQuery.sourceId}
              </span> */}
              </div>
            </div>
          )}

        {/* Query Details Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Query Details
          </span>
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Title
              </span>
              <span className="text-body font-body text-neutral-500">
                {localQuery.title}
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Description
              </span>
              <span className="text-body font-body text-neutral-500">
                {localQuery.description || "No description provided"}
              </span>
            </div>

            <div className="flex w-full items-center gap-6 flex-wrap">
              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Priority
                </span>
                <Badge
                  variant={
                    localQuery.priority === "high"
                      ? "error"
                      : localQuery.priority === "medium"
                      ? "warning"
                      : "neutral"
                  }
                >
                  {localQuery.priority.toUpperCase()}
                </Badge>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Raised By
                </span>
                <Badge className="bg-success-500 py-1 px-2">
                  <span className="text-purple">
                    {localQuery.raisedBy.userName.toUpperCase()}
                  </span>
                </Badge>
              </div>

              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Assigned To
                </span>
                <Badge className="bg-error-500 py-1 px-2">
                  <span className="text-white">
                    {localQuery.assignedTo?.userName.toUpperCase() ||
                      "UNASSIGNED"}
                  </span>
                </Badge>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Created
                </span>
                <span className="text-body font-body text-neutral-500">
                  {formatDate(localQuery.createdAt.toISOString())} •{" "}
                  {formatTime(localQuery.createdAt.toISOString())}
                </span>
              </div>
            </div>
          </div>
          {localQuery.priority === "high" && (
            <Alert
              variant="warning"
              icon={<FeatherBrain />}
              title="AI Suggestion"
              description="This is a high priority query. Consider escalating to management or assigning additional resources for faster resolution."
            />
          )}
        </div>

        {/* Query Timeline Section - Only show if there are subqueries */}
        {localQuery.subQueries && localQuery.subQueries.length > 0 && (
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Query Timeline
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-start gap-4">
                  <div className="flex flex-col items-center gap-2">
                    <IconWithBackground
                      variant="warning"
                      icon={<FeatherHelpCircle />}
                    />
                    <div className="flex w-px grow shrink-0 basis-0 flex-col items-center bg-neutral-200" />
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-white px-4 py-4">
                    <div className="flex w-full items-start justify-between">
                      <div className="flex flex-col items-start gap-2">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Main Query: {localQuery.title}
                        </span>
                      </div>
                      <QueryStatusBadge status={localQuery.status}>
                        {localQuery.status.charAt(0).toUpperCase() +
                          localQuery.status.slice(1).replace("_", " ")}
                      </QueryStatusBadge>
                    </div>
                    <span className="text-body font-body text-neutral-500">
                      {localQuery.description || "No description provided"}
                    </span>
                  </div>
                </div>
                {localQuery.subQueries.map((subquery, index) => (
                  <div
                    key={subquery.queryId}
                    className="flex w-full items-start gap-4 pl-8"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <IconWithBackground
                        icon={<FeatherGitBranch />}
                        variant={
                          subquery.status === "resolved" ? "success" : "neutral"
                        }
                      />
                      {index < localQuery.subQueries!.length - 1 && (
                        <div className="flex w-px grow shrink-0 basis-0 flex-col items-center bg-neutral-200" />
                      )}
                    </div>
                    <div
                      className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-white px-4 py-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                      onClick={() => handleSubQueryClick(subquery.queryId)}
                    >
                      <div className="flex w-full items-start justify-between">
                        <div className="flex flex-col items-start gap-2">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Sub-Query: {subquery.title}
                          </span>
                          <div className="flex items-center gap-2">
                            <Avatar size="x-small">
                              {subquery.raisedBy.userName
                                ?.substring(0, 2)
                                .toUpperCase() || "U"}
                            </Avatar>
                            <span className="text-caption font-caption text-subtext-color">
                              Created by {subquery.raisedBy.userName} •{" "}
                              {formatDate(subquery.createdAt.toISOString())}
                            </span>
                          </div>
                        </div>
                        <QueryStatusBadge status={subquery.status}>
                          {subquery.status === "resolved" ? "Resolved" : "New"}
                        </QueryStatusBadge>
                      </div>
                      <span className="text-body font-body text-neutral-500">
                        {subquery.description || "No description provided"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Attachments Section - Always show */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Attachments
            {localQuery.attachments && localQuery.attachments.length > 0
              ? ` (${localQuery.attachments.length})`
              : ""}
          </span>
          {localQuery.attachments && localQuery.attachments.length > 0 ? (
            <div className="flex w-full flex-col items-start gap-2">
              {localQuery.attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-white px-3 py-2"
                >
                  <FeatherFileText className="text-body font-body text-neutral-500" />
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      {attachment.file_name || `Document ${attachment.id}`}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {attachment.uploaded_at
                        ? getRelativeTime(attachment.uploaded_at)
                        : "Uploaded"}
                    </span>
                  </div>
                  {attachment.signed_url ? (
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherDownload />}
                      onClick={() => {
                        window.open(attachment.signed_url, "_blank");
                      }}
                    >
                      Download
                    </Button>
                  ) : (
                    <span className="text-caption font-caption text-subtext-color">
                      Loading...
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex w-full justify-center py-4">
              <span className="text-body text-neutral-500">
                No attachments uploaded yet
              </span>
            </div>
          )}
        </div>

        {/* Discussion Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Discussion
          </span>
          {!localQuery.comments || localQuery.comments.length === 0 ? (
            <div className="flex w-full justify-center py-4">
              <span className="text-body text-neutral-500">
                No comments yet
              </span>
            </div>
          ) : (
            <div className="flex w-full flex-col items-start gap-3">
              {localQuery.comments
                .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                .map((comment) => (
                  <div
                    key={comment.commentId}
                    className="flex w-full items-start gap-2"
                  >
                    <Avatar size="small">
                      {comment.user.userName
                        ?.split("@")[0]
                        ?.substring(0, 2)
                        ?.toUpperCase() || "U"}
                    </Avatar>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {comment.user.userName || "Unknown User"}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          {comment.createdAt &&
                          !isNaN(comment.createdAt.getTime())
                            ? getRelativeTime(comment.createdAt.toISOString())
                            : "Just now"}
                        </span>
                      </div>
                      <span className="text-body font-body text-default-font">
                        {comment.comment || "No comment text"}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
          <div className="flex w-full items-start gap-2">
            <Avatar size="small">CU</Avatar>
            <TextField
              className="h-auto grow shrink-0 basis-0"
              variant="filled"
              label=""
              helpText={
                submittingComment ? "Posting comment..." : "Press Enter to post"
              }
            >
              <TextField.Input
                placeholder="Add a comment..."
                value={comment}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setComment(e.target.value)
                }
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter" && !submittingComment) {
                    handleAddComment();
                  }
                }}
                disabled={submittingComment}
              />
            </TextField>
          </div>
        </div>
      </div>
      <QueryRaiseModal
        open={isRaiseQueryModalOpen}
        isCreateSubQuery={true}
        parentQueryId={queryId}
        onOpenChange={(open) => {
          setIsRaiseQueryModalOpen(open);
          if (!open) refetch();
        }}
      />
      <QueryDetailModal
        open={isSubQueryModalOpen}
        queryId={selectedSubQueryId}
        onOpenChange={(open) => {
          setIsSubQueryModalOpen(open);
          if (!open) refetch();
        }}
        onSubQueryUpdate={handleSubQueryUpdate}
        onQueryResolved={onQueryResolved}
        hideViewSourceButton={hideViewSourceButton}
      />
    </DialogLayout>
  );
};

export default QueryDetailModal;
